import { getConfig } from 'helpers/Config';
import { getSearchParamsAsObject, referrerOrigin } from 'helpers/Referrer';

/**
 * GA source referrer.
 * @type {String}
 */
let gaSource = 'unknown';

/**
 * GA default source referrer.
 * @type {String}
 */
export const GA_DEFAULT_SOURCE = 'unknown';

/**
 * Getting GA source for event.
 * @returns {String} ga source
 */
export function getSource() {
    return gaSource;
}

/**
 * Setting GA source for event.
 * @returns {String} ga source
 */
export function setSource(source) {
    gaSource = source;
}

function ga(...args) {
    if (window.ga) {
        window.ga(...args);
    } else {
        window.GoogleAnalyticsConfig.tagsQueue.push(args);
    }
}

/**
 * Set GA parameter with debug handling for logging.
 * @param  {String} params Params
 */
export function gaSet(params) {
    ga('set', params);
}

/**
 * Send GA events with debug handling for logging.
 * @param {Object} event Event sent
 * @param {Function} hitCallback Optional hit callback for being warned when GA acknowledged
 */
export function gaEvent(event, hitCallback) {
    const { category, action, label, ...restEvent } = event;
    if (typeof hitCallback === 'function') {
        if (window.ga) {
            window.ga('send', 'event', category, action, label, {
                hitCallback,
            });
        } else {
            hitCallback();
        }
    } else {
        ga('send', {
            hitType: 'event',
            eventCategory: category,
            eventAction: action,
            eventLabel: label,
            ...restEvent,
        });
    }
}

/**
 * Send GA page view with debug handling for logging.
 * @param  {String} path Path
 */
export function gaPageView(path) {
    ga('set', { page: path });
    ga('send', { hitType: 'pageview', page: path });
}

/**
 * Prepare event parameters from the clicked element
 * @param {Element} element The clicked element
 */
function uaEventHandler(element) {
    const parameters = ['category', 'action', 'eventLabel', 'value'].reduce((p, key) => {
        const value = element.getAttribute(`data-ua-event-${key}`);
        if (typeof value !== 'undefined' && value !== false) {
            // eslint-disable-next-line no-param-reassign
            p[key] = value;
        }
        return p;
    }, {});

    gaEvent(parameters);
}

/**
 * Init tracking on all click and for submit event
 */
function initializeTracking() {
    // Catch elements for Google Universal Analytics tracking
    document.addEventListener('click', (event) => {
        const element = event.target;
        if (element.hasAttribute('data-ua-hit-type')) {
            // eslint-disable-next-line prefer-rest-params
            uaEventHandler(element);
        }
    });
}

/**
 * Set dimensions depending on user.
 * @param {Object} analyticsUser User
 */
function setUserDimensions(analyticsUser) {
    gaSet({ dimension1: analyticsUser.type });
    if (analyticsUser.authenticated) {
        gaSet({ userId: analyticsUser.id });
        gaSet({ dimension2: analyticsUser.dates });
        gaSet({ dimension5: analyticsUser.segment });
    } else {
        gaSet({ dimension5: analyticsUser.type });
    }
    // Dimensions for pageTemplate/Category are set in sagas
    gaSet({ contentGroup2: 'estima' });
    gaSet({ dimension8: 'estima' });
}

/**
 * Initialize Google Analytics.
 * @param  {Object} analyticsUser User
 */
export function initAnalytics(analyticsUser) {
    if (!window.GoogleAnalyticsConfig) {
        window.GoogleAnalyticsConfig = {
            debug: getConfig('GA_DEBUG'),
            gaAccount: getConfig('GA_ACCOUNT'),
            tagsQueue: [],
        };
    }
    if (analyticsUser) {
        setUserDimensions(analyticsUser);
    }
    initializeTracking();

    gaSource = referrerOrigin(getSearchParamsAsObject());
}
